/**
 * useWatchHistoryData — fetches watch history via GET_USER_HISTORY
 * (userHistoryList query), transforms entries into EnrichedPlaylistItem[],
 * and exposes a remove handler that flags items as hidden.
 *
 * Mirrors the data-fetching pattern used by useProfilePlaylists for
 * Watch Later / Favorites so that watch history can render through
 * the generic SystemPlaylist component.
 */

import {
  useMemo, useCallback, useRef, useState,
} from 'react'
import _get from 'lodash/get'
import { useQuery, useMutation } from '@apollo/client'
import type { Reference } from '@apollo/client'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../../../apollo-graphql/index'
import { GET_USER_HISTORY } from '../../query'
import { FLAG_WATCHED_VIDEO } from '../../../playlist/query'
import { enrichWatchHistoryItems } from './watchHistoryEnrichment'
import useTitleGroupHydration from './useTitleGroupHydration'
import type {
  GetUserHistoryResponse,
  EnrichedPlaylistItem,
  UserHistoryEntry,
} from '../../types'

const WATCH_HISTORY_DEFAULT_LIMIT = 10

/**
 * Module-level stable empty array — handed back from the `data?.userMedia
 * PositionList?.userMediaPositions ?? EMPTY_HISTORY` fallback below.  A bare
 * `|| []` literal would allocate a fresh `[]` on every render while the
 * query is still in flight, which then ticks `hydrationItems`'s
 * `useMemo([watchHistory])` and the returned `watchHistoryItems` array's
 * `useMemo([watchHistory, contentMap])` deps — even though the content is
 * (still) empty.  Downstream that surfaced as ~10 redundant Watch History
 * row re-renders during the cold-load window before the query resolved.
 * Stabilize pre-data identity to keep parent prop deltas honest.
 */
const EMPTY_HISTORY: UserHistoryEntry[] = []

// ── Hook ─────────────────────────────────────────────────────────

interface UseWatchHistoryDataParams {
  language: string
  jwt: string
  isLoggedIn: boolean
  limit?: number
}

export default function useWatchHistoryData ({
  language,
  jwt,
  isLoggedIn,
  limit = WATCH_HISTORY_DEFAULT_LIMIT,
}: UseWatchHistoryDataParams) {
  const contentLanguage = useMemo(() => [language, '_primary'], [language])

  // cache-and-network: a return visit to /playlist/default paints the
  // cached watch-history list instantly while the background refetch
  // reconciles.  The userMediaPositionList typePolicy already merges
  // paginated results correctly, and flagWatchedVideo edits use
  // cache.modify so the cache stays accurate without a manual refetch.
  const {
    data, loading, fetchMore,
  } = useQuery<GetUserHistoryResponse>(GET_USER_HISTORY, {
    variables: {
      limit,
      offset: 0,
      contentLanguage,
    },
    skip: !isLoggedIn || !jwt,
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: { jwt },
  })

  const [flagWatchedVideo] = useMutation(FLAG_WATCHED_VIDEO, {
    context: { jwt },
  })

  // `?? EMPTY_HISTORY` (module-level stable ref) rather than `|| []` (fresh
  // literal each call) — see EMPTY_HISTORY docblock for the render-storm
  // rationale.  Once data resolves, Apollo's stable response array takes
  // over and identity tracks the cache as usual.
  const watchHistory = data?.userMediaPositionList?.userMediaPositions ?? EMPTY_HISTORY

  // Hydrate the difficulty-pill field that PR 2 dropped from
  // GET_USER_HISTORY.  cache-and-network GET_CONTENT shares its
  // Content:<id> entries with Tile.v2GraphqlManager / library tabs, so
  // returning visits hit the warm cache and the pill appears on the same
  // tick as the first paint.
  const hydrationItems = useMemo(() => {
    if (watchHistory.length === 0) return []
    const seen = new Set<string>()
    return watchHistory.reduce<Array<{ contentId: string; contentType: string }>>(
      (acc, entry) => {
        const featured = _get(entry, 'media.contentFeatured[0]', null)
        const cid = featured?.contentId != null ? String(featured.contentId) : ''
        const ctype = featured?.contentType ? String(featured.contentType) : ''
        if (!cid || !ctype) return acc
        const key = `${cid}-${ctype}`
        if (seen.has(key)) return acc
        seen.add(key)
        acc.push({ contentId: cid, contentType: ctype })
        return acc
      },
      [],
    )
  }, [watchHistory])
  // Per-row hydration via useTitleGroupHydration. Watch History pages in
  // 10s, so fanout engages once the user scrolls past the first page
  // (default threshold 24 would never trigger).
  const titleGroupIds = useMemo(
    () => hydrationItems.map(({ contentId, contentType }) => `${contentId}-${contentType}`),
    [hydrationItems],
  )
  const { contentMap } = useTitleGroupHydration(
    titleGroupIds,
    language,
    {
      skip: titleGroupIds.length === 0,
      jwt,
      fanoutThreshold: 12,
    },
  )

  const enrichedWatchHistory = useMemo(
    () => enrichWatchHistoryItems(watchHistory, contentMap),
    [watchHistory, contentMap],
  )

  // Stabilize the returned reference across Apollo re-emissions that don't
  // change the visible content.  `GET_USER_HISTORY` selects
  // `media.contentFeatured` which dereferences normalized `Content:<id>`
  // refs — so every `GET_CONTENT` hydration write (My Playlists, Currently
  // Watching, Followed Playlists) bumps `userMediaPositions`' reference
  // even when each entry is byte-identical.  Without this stabilization
  // each Apollo re-emit produces a new enriched array, defeating
  // `React.memo` on `SystemPlaylistRow` and causing the Watch History row
  // to re-render with byte-identical data (~4 post-data spurious renders
  // per cold load, observed via `[system-row]` diagnostics).
  //
  // Signature includes `contentMap.size` so a hydration that adds new
  // `Content:<id>` entries (restoring the difficulty pill) still flushes
  // through — pill-bearing tiles get a fresh ref on the same tick they
  // first have hydrated data to paint with.
  // Watch History ref-stability under Content cache writes.
  const stableRef = useRef<{ items: EnrichedPlaylistItem[]; sig: string }>({
    items: enrichedWatchHistory,
    sig: '',
  })
  const nextSig = `${enrichedWatchHistory.length}|${enrichedWatchHistory
    .map((i) => i.id)
    .join(',')}|m${contentMap.size}`
  if (nextSig !== stableRef.current.sig) {
    stableRef.current = { items: enrichedWatchHistory, sig: nextSig }
  }
  const watchHistoryItems = stableRef.current.items

  // Offset-based pagination — direct fetchMore with NO updateQuery.
  //
  // The cache typePolicy on `Query.userMediaPositionList` (apollo-graphql/index.js)
  // already concatenates pages when args.offset > 0. Running ALSO an
  // application-layer concat via `useApolloListPagination`'s `updateQuery`
  // produced a double-merge: Apollo wrote the pre-merged payload back through
  // the typePolicy, which appended the existing-page tiles a second time
  // (`[p1, p1, p2]`), or Apollo's identity check dropped the redundant write
  // and the watched query's `data` never re-emitted (sentinel re-fires forever
  // → "load more does not work"). Same failure mode `useFollowedPlaylists`
  // documented at lines 88-102 for the relay case; the fix is the same shape,
  // adapted to offset.
  //
  // With no `updateQuery`, the typePolicy sees `args.offset = nextOffset` and
  // appends `incoming.userMediaPositions` to `existing.userMediaPositions`
  // exactly once; Apollo re-emits `useQuery.data` and the sentinel unmounts
  // when `watchHistory.length >= count`.
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const isLoadingMoreRef = useRef(false)
  isLoadingMoreRef.current = isLoadingMore
  // Refs so `loadMore`'s identity stays constant across Apollo re-emits;
  // same render-storm concern documented at useApolloListPagination.ts:84-93.
  const dataRef = useRef(data)
  dataRef.current = data
  const contentLanguageRef = useRef(contentLanguage)
  contentLanguageRef.current = contentLanguage
  const limitRef = useRef(limit)
  limitRef.current = limit

  const loadMore = useCallback(async (): Promise<void> => {
    if (isLoadingMoreRef.current || !fetchMore) return
    const dataNow = dataRef.current
    if (!dataNow) return
    const loaded = dataNow.userMediaPositionList?.userMediaPositions?.length ?? 0
    const total = dataNow.userMediaPositionList?.count ?? 0
    if (loaded === 0) return
    if (total > 0 && loaded >= total) return
    setIsLoadingMore(true)
    try {
      await fetchMore({
        variables: {
          limit: limitRef.current,
          offset: loaded,
          contentLanguage: contentLanguageRef.current,
        },
      })
    } finally {
      setIsLoadingMore(false)
    }
  }, [fetchMore])

  const watchHistoryTotalDuration = useMemo(() => {
    let totalSeconds = 0
    watchHistory.forEach((entry) => {
      totalSeconds += _get(entry, 'media.duration', 0)
    })
    if (totalSeconds <= 0) return null
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    if (hours > 0) return `${hours}h ${minutes}m`
    return `${minutes}m`
  }, [watchHistory])

  const handleRemoveWatchHistoryItem = useCallback(async (item: EnrichedPlaylistItem) => {
    // Watch-history tiles are one-per-video; Remove fires a single
    // `flagWatchedVideo` for the tile's contentId. The card is hidden on
    // success and stays visible on failure (matching the custom-playlist
    // remove handler's fail-quiet behavior).  Cache stays accurate via
    // `cache.modify` on the `Query.userMediaPositionList` field — a bare
    // refetch() would revert to the base variables (offset:0, limit) and
    // discard every fetchMore page, costing 200–800ms and the user's
    // scroll position past page 1.
    //
    // The cache stores userMediaPositions inline (not as Refs), each with a
    // nested `media.contentFeatured(...)` array whose [0].contentId is the
    // same Brooklyn video id we pass to the mutation.  Walk that path
    // directly to find the row to drop — both the position-row's `mediaId`
    // (asset id) and the enriched item's `mediaId` / `featureMedia.mediaId`
    // get collapsed to the contentId by `watchHistoryEnrichment.ts:88-94`,
    // so matching by mediaId from the item is unreliable.
    const videoId = Number(item.contentId)
    if (!Number.isFinite(videoId) || videoId <= 0) return
    try {
      await flagWatchedVideo({
        variables: { videoId, hiddenByUser: true },
        update: (cache) => {
          cache.modify({
            fields: {
              userMediaPositionList (existing) {
                if (!existing || typeof existing !== 'object') return existing
                const list = existing as {
                  userMediaPositions?: readonly Reference[]
                  count?: number
                }
                if (!Array.isArray(list.userMediaPositions)) return existing
                const before = list.userMediaPositions.length
                const filtered = list.userMediaPositions.filter((entry) => {
                  // Inline entry; `media` is the Media object with the
                  // arg-keyed `contentFeatured(...)` field.  Find it by
                  // prefix since the args vary per query.
                  const e = entry as { media?: Record<string, unknown> }
                  if (!e.media) return true
                  const cfKey = Object.keys(e.media).find(
                    (k) => k.startsWith('contentFeatured'),
                  )
                  if (!cfKey) return true
                  const contentFeatured = e.media[cfKey] as
                    | Array<{ contentId?: number | string }>
                    | undefined
                  const cid = contentFeatured?.[0]?.contentId
                  return Number(cid) !== videoId
                })
                if (filtered.length === before) return existing
                return {
                  ...list,
                  userMediaPositions: filtered,
                  count: Math.max(0, (list.count ?? 1) - 1),
                }
              },
            },
          })
        },
      })
    } catch {
      // mutation failed — item stays visible
    }
  }, [flagWatchedVideo])

  // Derive lastBatchSize from the total count vs loaded items.
  // When loaded < total → more pages exist → signal lastBatchSize >= limit
  // When loaded >= total → no more pages → signal lastBatchSize < limit
  const totalAvailable = data?.userMediaPositionList?.count ?? 0
  const hasMorePages = watchHistory.length > 0 && watchHistory.length < totalAvailable
  const lastBatchSize = hasMorePages ? limit : 0

  return {
    watchHistoryItems,
    watchHistoryTotalCount: data?.userMediaPositionList?.count ?? watchHistoryItems.length,
    watchHistoryTotalDuration,
    watchHistoryLoading: loading && !data,
    onRemoveWatchHistoryItem: handleRemoveWatchHistoryItem,
    loadMore,
    lastBatchSize,
    isLoadingMore,
  }
}
